import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button, Field, FormDialog, Textarea, DialogCancel } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { CrewInviteePicker } from "../dashboard/CrewInviteePicker.js";
import { ReleasePicker } from "../releases/ReleasePicker.js";
import {
  useCreateTask,
  TASK_TITLE_MAX,
  TASK_NOTES_MAX,
  type SaveTaskRequest,
} from "../../app/hooks/useTasks.js";
import styles from "./TaskCreateModal.module.scss";

/**
 * The "+ New task" create modal — title, notes, due date, release, assignees. FIVE FIELDS, deliberately:
 * links and the audience flags used to live here and now belong to the drawer, where the task is edited for
 * the rest of its life. A create form that collects everything makes the common case (a title and a date)
 * feel like paperwork, and it splits each control's implementation across two surfaces.
 *
 * The RELEASE field is the exception to that trimming, added 2026-08-23 on operator request, and it earns
 * its place on a different argument: `release_id` is the one field here that CANNOT be set later. The
 * update body deliberately omits it (`UpdateTaskRequest` — "release_id / phase / position are absent from
 * the body by design"), so a task created without one is standalone forever. Before this, the only way to
 * link a task to a rollout was to create it from that release's own To-do; a task started on the dashboard —
 * which is where most of them start — could never be attached to one at all. Create is therefore the only
 * moment the choice exists, which is precisely when a field for it belongs.
 *
 * Rendered in the shared
 * `@cntrl/ui` `Modal`
 * (FE-SH-02), so the scrim, the Escape STACK, the ref-counted body-scroll lock and the focus trap/restore
 * all live in the one tested primitive — this file owns only the body. Create is server-confirm THEN repaint
 * (a pending state, not optimistic); it closes ONLY on a persisted write and opens the new task's drawer. A
 * server error stays open and reports verbatim (never a phantom success).
 */
export function TaskCreateModal({
  onClose,
  onCreated,
  releaseId,
}: {
  onClose: () => void;
  onCreated?: (taskId: string) => void;
  /** When set, the created task is scoped to this release (the release To-do "+ Add Task") and the picker
   *  does not render — the surface already answered the question, and offering a control that could
   *  re-target the task away from the To-do it was opened from is a trap, not a choice. */
  releaseId?: string;
}) {
  const create = useCreateTask();
  // The catalogue is `releases:read`, which the touring crew does not hold (it is the content + owner
  // audience). A seat without it gets no field rather than a permanently-empty one that reads as "you have
  // no releases" — and the server would 403 the list anyway.
  const canLinkRelease = useCapability("releases:read") && !releaseId;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignees, setAssignees] = useState<string[]>([]);
  const [linkedRelease, setLinkedRelease] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const titleRef = useRef<HTMLInputElement>(null);

  // `useModalA11y` focuses the first focusable in the dialog, which is the header's close button. The title is
  // the real entry point, so claim it — this effect runs after the primitive's (parent effects run last).
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError("Give the task a title.");
      titleRef.current?.focus();
      return;
    }
    const body: SaveTaskRequest = {
      title: title.trim(),
      description: description.trim() || null,
      dueDate: dueDate || null,
      releaseId: releaseId ?? linkedRelease,
      assignees: assignees.map((personId) => ({ personId })),
    };
    create.mutate(body, {
      onSuccess: (task) => {
        if (task.taskId && onCreated) onCreated(task.taskId);
        else onClose();
      },
      onError: (err) => setError(err.message),
    });
  }

  const actions = (
    <>
      <DialogCancel />
      <Button
        type="submit"
        loading={create.isPending}
        disabled={!title.trim()}
      >
        Create task
      </Button>
    </>
  );

  const fields = (
    <>
      <Field
          ref={titleRef}
          label="Title"
          placeholder="e.g. Approve album art"
          maxLength={TASK_TITLE_MAX}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <Textarea
          label="Notes (Optional)"
          rows={3}
          placeholder="What needs doing?"
          maxLength={TASK_NOTES_MAX}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <Field
          label="Due date (Optional)"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />

        {/* The rollout this task belongs to, when it is one. Absent on the release page's own To-do (the
                release is already known there) and for a seat without `releases:read`. */}
        {canLinkRelease && (
          <ReleasePicker value={linkedRelease} onChange={setLinkedRelease} />
        )}

        {/* Assignees. SELF is a candidate (unlike an opportunity, a task assigned to yourself is the common
                case) and the OWNER is not: the artist's person record is named the band, and the band is not
                crew. The label carries the optionality, so no hint line.

                source="assignable" is the load-bearing part: the list is the assignment GATE read forwards, so
                it spans band AND org seats and is narrowed by this caller's own role. On the crew source — what
                this used until 2026-09-04 — a booking agent could not offer a management person the save would
                have accepted, and an intern was offered the whole crew and refused on every pick. */}
        <CrewInviteePicker
          label="Assignees (Optional)"
          searchPlaceholder="Search people…"
          source="assignable"
          includeSelf
          excludeOwner
          value={assignees}
          onChange={setAssignees}
        />

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
    </>
  );

  // A FormDialog: a Modal on desktop, a bottom Sheet on a phone (ART-251 — a centred card with the keyboard up
  // left this form covered and unscrollable).
  return (
    <FormDialog
      title="New task"
      size="md"
      onClose={onClose}
      bodyAs="form"
      onSubmit={onSubmit}
      footer={actions}
    >
      {fields}
    </FormDialog>
  );
}
